import { inject, injectable } from 'inversify';

import { UrlUtils } from '../utils/url.utils';
import { devProxyOrigin, isDevProxyEnabled } from './dev-proxy';
import { EnvironmentService } from './environment.service';

export type DomainType = 'offline';

@injectable()
export class ApiConfig {
    static apiPathPrefix = '/v1/api';
    static edgePathPrefix = '/v2/edge';
    static fileViewPathPrefix = '/downloads';

    static localhostDomainName = '127.0.0.1';

    get apiDomainName() {
        return this.envService && this.envService.apiDomainname();
    }

    get podDomainName() {
        return this.envService && this.envService.podDomainname();
    }

    get apiSubdomain() {
        return 'api-v1';
    }

    get qaApiSubdomain() {
        return 'qa-api-v1';
    }

    get subdomainPrefixForNCF() {
        return 'ncf-'
    }

    get podSubdomain() {
        return 'pod'
    }

    get qaPodSubdomain() {
        return 'qa-pod'
    }

    // TODO change to readonly
    private get QA_API_ENDPOINT_1() { return `https://qa-1.${this.apiDomainName}`; }

    private get QA_WS_ENDPOINT_3() { return `https://qa-2.${this.apiDomainName}`; }

    apiEndpoint: string | null = null;
    podEndpoint: string | null = null;
    // TODO enable this

    constructor(
        @inject(EnvironmentService) private envService: EnvironmentService,
    ) {
        this.apiEndpoint = `https://${this.apiSubdomain}.${this.apiDomainName}`;
        this.podEndpoint = `https://${this.podSubdomain}.${this.podDomainName}`;
        try {
            if (isDevProxyEnabled()) {
                // Dev inner loop (2026-09-03). The browser must only ever talk to the dev
                // server's own origin — every backend host answers a localhost `Origin` with a
                // CORS denial (measured: qa-api-v1 replies
                // `Access-Control-Allow-Origin: https://www.xconvert.com`). An empty
                // `apiEndpoint` makes every `apiEndpoint + '/v1/api/...'` helper below emit a
                // relative URL, which next.config.js's dev-only `rewrites()` forwards to QA
                // server-side, where no CORS check applies. The pod is host-addressed because
                // it shares the `/v2/edge` prefix with the runtime-chosen edge hosts.
                this.apiEndpoint = '';
                this.podEndpoint = devProxyOrigin(`${this.qaPodSubdomain}.${this.podDomainName}`);
            } else if (ApiConfig.isLocalhost() || this.envService.env.dev) {
                this.apiEndpoint = `https://${this.envService.env.localhostAPIDomain}`;
                this.podEndpoint = `https://${this.envService.env.localhostPODDomain}`;
            } else if (ApiConfig.isQA()) {
                this.apiEndpoint = `https://${this.qaApiSubdomain}.${this.apiDomainName}`;
                this.podEndpoint = `https://${this.qaPodSubdomain}.${this.podDomainName}`;
            } else {
            }
        } catch (e) {
            console.error('Suppressed ApiConfig constructor error: ' + e.message);
        }
    }

    getLogoutEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/auth/logout`;
    }

    getLoginEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/auth/login`;
    }

    getRefreshEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/auth/refresh`;
    }

    getReauthEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/auth/reauth`;
    }

    getAuthenticateEndpoint(): string {
        return `${this.getApiHttpsDomain()}${ApiConfig.apiPathPrefix}/auth/authenticate`;
    }

    getWebSocketEndpoint(mode?: string): string {
        // `envService.env` is populated asynchronously from the bootstrap
        // window object; the constructor caught a "Suppressed ApiConfig
        // constructor error" earlier, so `env` can legitimately be
        // undefined when admin pages mount before settings have published.
        // Guard with optional chaining + the host-based ApiConfig.isQA
        // helper so the host parsing alone is enough to pick the right
        // endpoint set.
        const isQa = this.envService.env?.qa || ApiConfig.isQA();
        const isDev = this.envService.env?.dev || false;
        if (ApiConfig.isLocalhost() || isDev || isQa) {
            // 2026-09-02: was `/comm/ws-domain`, a path no professorx build serves — every QA
            // page load logged three API_ERROR(404)s for it (found by the new API_ERROR event
            // on the first QA run). QA and prod resolve through the same controller.
            return `${this.getApiWSQADomains()}${ApiConfig.apiPathPrefix}/servers/resolve/ws-domain`;
        }

        return `${this.getApiWSProductionDomains()}${ApiConfig.apiPathPrefix}/servers/resolve/ws-domain`;
    }

    getHttpsEndpoint(mode?: DomainType): string {
        // See getWebSocketEndpoint for the env-undefined rationale.
        const isQa = this.envService.env?.qa || ApiConfig.isQA();
        if (isQa) {
            return this.getQAApiHttpsDomain();
        }
        return `${this.getApiHttpsDomain()}${ApiConfig.apiPathPrefix}`;
    }

    /**
     * THE origin resolver for a runtime-chosen backend host (an ironman edge from
     * `/v1/api/servers/resolve`, or an ncf domain). `https://<host>` when deployed;
     * a same-origin dev-proxy path while `isDevProxyEnabled()` — see services/dev-proxy.ts.
     */
    static originForDomain(domain: string) {
        return isDevProxyEnabled() ? devProxyOrigin(domain) : UrlUtils.toHttpsProtocol(domain);
    }

    static suffixDomainWithHttpsEdgeEndpoint(domain: string) {
        return `${ApiConfig.originForDomain(domain)}${ApiConfig.edgePathPrefix}`;
    }

    static suffixDomainWithHttpsViewEndpoint(domain: string) {
        return `${ApiConfig.originForDomain(domain)}${ApiConfig.fileViewPathPrefix}`;
    }

    static isLocalhost(): boolean {
        try {
            return window.location.host.startsWith('localhost');
        } catch {
            return false;
        }
    }

    static isQA(): boolean {
        try {
            const host = window.location.host;
            return host.startsWith('qa') || host.startsWith('ncf-qa');
        } catch {
            return false;
        }
    }

    private getApiHttpsDomain(): string {
        return this.apiEndpoint;
    }

    private getQAApiHttpsDomain(): string {
        // professorx is deployed with `server.servlet.context-path=/v1/api`
        // in every environment, so every controller mapping is exposed at
        // `/v1/api/...`. The PROD branch of `getHttpsEndpoint()` correctly
        // appends `apiPathPrefix`; the QA branch had been returning the bare
        // host (`https://qa-1.xconvert.com`) which made every
        // `adminUrl(path)` call resolve to `/admin/...` (no prefix) → 404.
        return `${this.QA_API_ENDPOINT_1}${ApiConfig.apiPathPrefix}`;
    }

    private getApiWSQADomains(): string {
        return this.apiEndpoint;
    }

    private getApiWSProductionDomains(): string {
        return this.apiEndpoint;
    }

    private getQAApiWSDomains(): string {
        return this.QA_WS_ENDPOINT_3;
    }

    getPaymentsPayEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/payments/pay`;
    }

    getPaymentsConfirmStripeEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/payments/confirm-stripe`;
    }

    getPaymentsPayPalEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/payments/pay-paypal`;
    }

    getCancelPaymentsEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/payments/cancel`;
    }

    getRegisterEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/auth/register`;
    }

    getEmailValidateEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/auth/validate-email`;
    }

    getUserDetailsEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/auth/user-details`;
    }

    getRecoverPasswordEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/auth/recover-password`;
    }

    getResetPasswordEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/auth/reset-password`;
    }

    getAccountVerifyEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/auth/verify`;
    }

    getProcessAuthEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/auth/authorize-request`;
    }

    getSupportEmailEndpoint() {
        return this.podEndpoint + `${ApiConfig.edgePathPrefix}/comm/captcha_validate`;
    }

    getHistoryTrackEndpoint() {
        return this.podEndpoint + `${ApiConfig.edgePathPrefix}/comm/history-track/v2`;
    }

    getUserTraceEndpoint() {
        return this.podEndpoint + `${ApiConfig.edgePathPrefix}/users/trace`;
    }

    getVersionCheckEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/settings/web_version`;
    }

    getAdminWorkflowSearchEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/admin/workflows/search`;
    }

    getAdminWorkflowRecreateEndpoint(workflowId: string) {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/admin/workflows/${workflowId}/recreate`;
    }

    getUploadAndProcessEndpointResolveEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/servers/resolve`;
    }

    getScheduleTaskEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/tasks/schedule`;
    }

    generateFileId() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/uploads/new-file-id`;
    }

    getUserSettingsEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/settings`;
    }

    getFeatureSearchEndpoint(query: string) {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/search/features?q=${encodeURIComponent(query)}`;
    }

    getMetricsEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/metrics/events`;
    }

    getUserAnalyticsSaveEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/comm/analytics`;
    }

    getMotiveStatEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/comm/motive`;
    }

    getGlobalStatsEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/stats/global`;
    }

    // ================================================================
    // Developer API portal — ApiKeys + WebhookEndpoints (§14 E1-E3)
    // ================================================================
    //
    // These hit the portal-user-scoped routes on professorx
    // (authed via the session JWT, not a public ApiKey). The portal
    // UI manages the customer's own keys + webhook endpoints; the
    // /v1/public/* routes the keys are used against are a separate
    // surface.

    getApiKeysEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/portal/api-keys`;
    }

    // ================================================================
    // Manage Devices — auth-refresh Task 23
    // ================================================================

    /**
     * `GET /v1/api/portal/devices` — list active sessions for the
     * signed-in user. Backed by `ManageDevicesController` on professorx.
     */
    getDevicesEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/portal/devices`;
    }

    /**
     * `POST /v1/api/portal/devices/revoke` — revoke one session.
     * Body: `{ sessionId: string }` (MongoDB ObjectId hex string).
     */
    getRevokeDeviceEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/portal/devices/revoke`;
    }

    /**
     * Portal conversion-history endpoint. Backed by
     * `ConversionHistoryController` on professorx. Session-authenticated
     * (same as `/me/billing`).
     *
     *   `source`  — optional "website" | "api_key" filter
     *   `limit`   — capped server-side at 200
     */
    getConversionHistoryEndpoint(
        source?: 'website' | 'api_key',
        limit?: number,
        from?: string,
        until?: string,
    ) {
        const base = this.apiEndpoint + `${ApiConfig.apiPathPrefix}/portal/conversion-history`;
        const params: string[] = [];
        if (source) params.push(`source=${encodeURIComponent(source)}`);
        if (limit) params.push(`limit=${limit}`);
        if (from) params.push(`from=${encodeURIComponent(from)}`);
        if (until) params.push(`until=${encodeURIComponent(until)}`);
        return params.length > 0 ? `${base}?${params.join('&')}` : base;
    }

    // ===== Billing V2 — payments-redesign.md §F11 / §F12 =====
    // professorx is deployed with `server.servlet.context-path=/v1/api`, so
    // every controller mapping (`/me/billing`, `/admin/...`) is exposed at
    // `/v1/api/<mapping>`. Earlier versions of these helpers built URLs
    // without the prefix and only worked in environments where an upstream
    // nginx rewrite papered over the gap; QA has no such rewrite and the
    // bare-path calls returned 404. Always prepend `apiPathPrefix`.

    getMeBillingEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/me/billing`;
    }

    getMeBillingHistoryEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/me/billing/history`;
    }

    getMeBillingUsageEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/me/billing/usage`;
    }

    getMeBillingGrantsEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/me/billing/grants`;
    }

    getMeBillingExportJsonEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/me/billing/export.json`;
    }

    getMeBillingExportCsvEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/me/billing/export.csv`;
    }

    getMeBillingTopupEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/me/billing/topup`;
    }

    getMeBillingTopupPacksEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/me/billing/topup/packs`;
    }

    getMeBillingApiSubscriptionEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/me/billing/api-subscription`;
    }

    getMeBillingApiSubscriptionPlansEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/me/billing/api-subscription/plans`;
    }

    getMeBillingApiSubscriptionCancelEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/me/billing/api-subscription/cancel`;
    }

    getMeBillingWebsitePlansEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/me/billing/website-plans`;
    }

    /**
     * GET /me/billing/usage-7day — 7-day usage sparkline for the
     * Overview page (Phase 4 Task 4.4 frontend wrapper / Task 4.5
     * backend). Session-authenticated, no query parameters.
     */
    getUsageLast7DaysEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/me/billing/usage-7day`;
    }

    // Admin billing endpoints (Stream B B5/B6/B7).
    getAdminUserBillingOverviewEndpoint(userId: string) {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/admin/users/${encodeURIComponent(userId)}/billing`;
    }

    getAdminUserBillingGrantEntitlementEndpoint(userId: string) {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/admin/users/${encodeURIComponent(userId)}/billing/grant-entitlement`;
    }

    getAdminUserBillingRevokeEntitlementEndpoint(userId: string) {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/admin/users/${encodeURIComponent(userId)}/billing/revoke-entitlement`;
    }

    getAdminUserBillingGrantCreditsEndpoint(userId: string) {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/admin/users/${encodeURIComponent(userId)}/billing/grant-credits`;
    }

    getAdminBillingGrantBulkEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/admin/billing/grant-bulk`;
    }

    getAdminBillingValidateEndpoint(userId: string) {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/admin/billing/validate/${encodeURIComponent(userId)}`;
    }

    getApiKeyByIdEndpoint(id: string) {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/portal/api-keys/${encodeURIComponent(id)}`;
    }

    getApiKeyRotateEndpoint(id: string) {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/portal/api-keys/${encodeURIComponent(id)}/rotate`;
    }

    getApiKeyRevokeEndpoint(id: string) {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/portal/api-keys/${encodeURIComponent(id)}/revoke`;
    }

    getWebhookEndpointsEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/portal/webhook-endpoints`;
    }

    getWebhookEndpointByIdEndpoint(id: string) {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/portal/webhook-endpoints/${encodeURIComponent(id)}`;
    }

    getWebhookEndpointRotateSecretEndpoint(id: string) {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/portal/webhook-endpoints/${encodeURIComponent(id)}/rotate-secret`;
    }

    getWebhookEndpointResumeEndpoint(id: string) {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/portal/webhook-endpoints/${encodeURIComponent(id)}/resume`;
    }

    getWebhookEndpointAckSecretEndpoint(id: string) {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/portal/webhook-endpoints/${encodeURIComponent(id)}/acknowledge-secret`;
    }

    getWebhookDeliveriesEndpoint(endpointId?: string) {
        const base = this.apiEndpoint + `${ApiConfig.apiPathPrefix}/portal/webhook-deliveries`;
        return endpointId ? `${base}?endpointId=${encodeURIComponent(endpointId)}` : base;
    }

    getWebhookDeliveryRedeliverEndpoint(id: string) {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/portal/webhook-deliveries/${encodeURIComponent(id)}/redeliver`;
    }

    getBulkZipBaseEndpoint() {
        return this.apiEndpoint + `${ApiConfig.apiPathPrefix}/bulk-zip`;
    }
}
